import React, { Fragment, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Icon } from '@arnsbogroup/react-voicearchive'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import useUrl from 'useUrl'
import { useBrand } from 'contexts/BrandContext'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import projectMessages from 'containers/ProjectPage/messages'

export default NiceModal.create(({ project, location }) => {
  const { formatMessage, locale } = useIntl()
  const brand = useBrand()
  const modal = useModal()
  const uriRefs = useRef([])
  const [copied, setCopied] = useState([])

  const onCopyToClipBoard = (i) => {
    uriRefs.current[i].select()
    document.execCommand('copy')
    setCopied((prev) => [...prev, i])

    setTimeout(() => {
      setCopied((prev) => prev.filter((n) => n !== i))
    }, 2000)
  }

  const embeddedCodeSnippet = `
    <iframe src="${useUrl(locale, '/embedded', true, false, brand)}/${project.hashId}" width="100%" height="100%"></iframe>
  `
  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
    >
      <div className="space-y-4 bg-white" style={{ zIndex: 100 }}>
        { [
          ['', 'Default view', 'Shows banner with headline, voices, characters and branding if applied.', false],
          ['', 'Anonymous view', 'Uses anonymous link. Shows banner with headline, voices, characters and branding if applied.', true],
          ['/' + formatMessage(projectMessages.feedbackPath), 'Collect feedback', 'This link will show thumbs up/down voting feature and prompt visitor to submit their feedback.', false],
        ].map(([path, name, description, isAnonymous], i) => (
          <div key={path + i} className="space-y-2">
            <h4 className="font-medium text-sm">{ name }</h4>

            <p className="text-sm">{ description }</p>

            <div className="bg-gray-100 p-2 text-sm flex w-full">
              <div className="cursor-pointer col-span-5 w-3/4">
                <input
                  type="uri"
                  value={useUrl(locale, location.pathname, true, isAnonymous, brand) + path}
                  className="w-full h-auto bg-transparent text-gray-700"
                  ref={(ref) => uriRefs.current[i] = ref}
                  onClick={() => uriRefs.current[i].select()}
                  readOnly
                />
              </div>

              <div className="px-2 space-x-2 text-gray-600 w-1/4 text-right">
                <Icon
                  icon={copied.includes(i) ? 'check' : 'share'}
                  onClick={() => onCopyToClipBoard(i)}
                  className="hover:text-primary cursor-pointer"
                />
              </div>
            </div>
          </div>
        )) }

        <div className="space-y-2">
          <h4 className="font-medium text-sm">Embedded</h4>

          <p className="text-sm">Send this to client, to allow them to embed voice finder directly on their website.</p>

          <p className="font-mono text-sm bg-gray-100 p-2 break-all">
            { embeddedCodeSnippet }
          </p>
        </div>

        <Button size="sm" onClick={() => modal.remove()}>
          Close
        </Button>
      </div>
    </Modal>
  )
})




// Imported directly until react-voicearchive is up-to-date with AME
const Modal = ({
  children,
  show = false,
  title = '',
  childClassName = 'px-4 pt-5 pb-4 sm:p-6',
  onClose = () => {},
  onOutsideClick = () => {},
  dialogClasses = '',
  backdropClasses = '',
  dialogPanelClasses = '',
  dialogTitleClasses = '',
  closeButtonClasses = '',
  ...props
}) => {

  function tryOnClose() {
    if (onClose) {
      onClose()
    }
  }

  function tryOnOutsideClose() {
    if (onOutsideClick) {
      onOutsideClick()
    }
  }

  return (
    <>
      <Transition appear show={show} as={Fragment}>
        <Dialog as="div" className={classNames("relative z-10", dialogClasses)} onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={classNames("fixed inset-0 bg-gray-200 bg-opacity-75", backdropClasses)}/>
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div 
              className="flex min-h-full items-center justify-center p-4 text-center"
              onClick={tryOnOutsideClose}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className={classNames("w-full max-w-md transform rounded bg-white px-4 pt-5 pb-4 sm:p-6 text-left align-middle shadow-xl transition-all", dialogPanelClasses)}>

                  {typeof onClose === 'function' && (
                    <div className="absolute top-0 right-0">
                      <button
                        type="button"
                        className={classNames("px-4 py-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500", closeButtonClasses)}
                        onClick={tryOnClose}
                      >
                        <Icon icon={['fas', 'times']} />
                      </button>
                    </div>
                  )}

                  {!!title && (
                    <Dialog.Title as="h3" className={classNames("text-lg font-medium mb-2", dialogTitleClasses)}>{title}</Dialog.Title>
                  )}

                  {children}

                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
